// ============================================================================
// EcgExplainabilitySections.tsx — серверный слой объяснимости в «Почему»:
// критерии и пороговые отклонения, достаточность сигнала для модели,
// уверенность, атрибуция отведений/времени и текстовое обоснование.
//
// Показываются ТОЛЬКО поля ответа сервиса; отсутствующие значения не
// подставляются. Если атрибуции нет — выводится причина, а не выдуманные веса.
// ============================================================================

import type {
    EcgAttribution,
    EcgAttributionSegment,
    EcgConfidence,
    EcgCriterion,
    EcgExplainability,
    EcgRationale,
    EcgSignalQualityExplanation,
} from '@/lib/ecgAnalysis';

const CRITERION_STATUS: Record<EcgCriterion['status'], { label: string; tone: string }> = {
    within: { label: 'в норме', tone: 'text-emerald-700 border-emerald-500/40 bg-emerald-500/10' },
    below: { label: 'ниже порога', tone: 'text-amber-700 border-amber-500/40 bg-amber-500/10' },
    above: { label: 'выше порога', tone: 'text-amber-700 border-amber-500/40 bg-amber-500/10' },
    unknown: { label: 'не рассчитано', tone: 'text-muted-foreground border-border bg-muted/30' },
};

const CONFIDENCE_LEVEL: Record<string, string> = {
    high: 'высокая',
    medium: 'средняя',
    low: 'низкая',
    insufficient: 'недостаточно данных',
};

const COMPONENT_LABELS: Record<string, string> = {
    image_quality: 'качество снимка',
    leads_completeness: 'полнота отведений',
    duration_coverage: 'длительность записи',
    calibration: 'калибровка',
    measurements_completeness: 'измерения',
    model_signal_gate: 'проверка сигнала моделью',
};

function fmtPercent(value: number | null): string {
    if (value === null || !Number.isFinite(value)) return '—';
    return `${Math.round(value * 100)}%`;
}

function fmtSeconds(value: number | null): string {
    if (value === null || !Number.isFinite(value)) return '—';
    return `${value.toFixed(2)} с`;
}

function fmtNumber(value: number | null, digits = 1): string {
    if (value === null || !Number.isFinite(value)) return '—';
    return value.toFixed(digits);
}

function Block({ title, children }: { title: string; children: React.ReactNode }) {
    return (
        <div className="space-y-1.5">
            <h4 className="text-[11px] uppercase tracking-wide text-muted-foreground">{title}</h4>
            {children}
        </div>
    );
}

function CriterionRow({ item }: { item: EcgCriterion }) {
    const state = CRITERION_STATUS[item.status] || CRITERION_STATUS.unknown;
    const reference: string[] = [];
    if (item.reference.low !== null) {
        reference.push(`${item.reference.lowInclusive ? '≥' : '>'} ${item.reference.low}`);
    }
    if (item.reference.high !== null) {
        reference.push(`${item.reference.highInclusive ? '≤' : '<'} ${item.reference.high}`);
    }
    const deviation = item.deviationAbs !== null && item.deviationAbs > 0
        ? `${item.deviation !== null && item.deviation > 0 ? '+' : '−'}${Math.abs(item.deviation ?? item.deviationAbs).toFixed(1)} ${item.deviationUnit}`
        : '';
    return (
        <li className="px-3 py-2 space-y-1">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-medium text-sm">{item.label}</span>
                <span className={`text-[11px] border rounded px-1.5 py-0.5 ${state.tone}`}>{state.label}</span>
            </div>
            <div className="text-xs text-muted-foreground">
                {item.value !== null
                    ? `значение ${fmtNumber(item.value)} ${item.unit}`
                    : 'значение не рассчитано'}
                {reference.length > 0 && ` · порог ${reference.join(' и ')} ${item.unit}`}
                {deviation && ` · отклонение ${deviation}`}
            </div>
            <div className="text-xs">{item.explanation}</div>
            {item.note && <div className="text-[11px] text-muted-foreground">{item.note}</div>}
        </li>
    );
}

export function CriteriaEvidence({ explainability }: { explainability: EcgExplainability }) {
    const criteria = explainability.criteria;
    if (criteria.length === 0) {
        return (
            <Block title="Критерии и пороги">
                <div className="text-sm text-muted-foreground">
                    Сервис не передал критериальный слой — пороговые отклонения недоступны.
                </div>
            </Block>
        );
    }
    return (
        <Block title="Критерии и пороги">
            <div className="text-[11px] text-muted-foreground">
                Справочные ориентиры по измеренным значениям.
            </div>
            <ul className="border rounded-md divide-y text-sm">
                {criteria.map((item) => <CriterionRow key={item.code} item={item} />)}
            </ul>
        </Block>
    );
}

function ConfidenceComponentRow({ item }: { item: EcgConfidence['components'][number] }) {
    return (
        <tr>
            <td className="px-2 py-1">{COMPONENT_LABELS[item.code] || item.code}</td>
            <td className="px-2 py-1 text-right text-muted-foreground">{fmtPercent(item.weight)}</td>
            <td className="px-2 py-1 text-right">{fmtPercent(item.score)}</td>
            <td className="px-2 py-1 text-muted-foreground">{item.detail}</td>
        </tr>
    );
}

export function ConfidenceEvidence({ confidence }: { confidence: EcgConfidence }) {
    const level = CONFIDENCE_LEVEL[confidence.level] || confidence.level || 'не указана';
    return (
        <Block title="Уверенность в данных">
            <div className="text-sm">
                {level} · {fmtPercent(confidence.score)}
                <span className="text-muted-foreground"> (описательный балл достаточности данных)</span>
            </div>
            {confidence.meaning && (
                <div className="text-[11px] text-muted-foreground">{confidence.meaning}</div>
            )}
            {confidence.caps.length > 0 && (
                <ul className="list-disc pl-5 text-xs text-amber-700 dark:text-amber-400 space-y-0.5">
                    {confidence.caps.map((cap) => <li key={cap}>{cap}</li>)}
                </ul>
            )}
            <div className="border rounded-md overflow-x-auto">
                <table className="w-full text-xs">
                    <thead className="bg-muted/40 text-muted-foreground">
                        <tr>
                            <th className="text-left font-medium px-2 py-1">Фактор</th>
                            <th className="text-right font-medium px-2 py-1">Вес</th>
                            <th className="text-right font-medium px-2 py-1">Балл</th>
                            <th className="text-left font-medium px-2 py-1">Что учтено</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y">
                        {confidence.components.map((item) => (
                            <ConfidenceComponentRow key={item.code} item={item} />
                        ))}
                    </tbody>
                </table>
            </div>
            {confidence.limitations.length > 0 && (
                <ul className="list-disc pl-5 text-[11px] text-muted-foreground space-y-0.5">
                    {confidence.limitations.map((item) => <li key={item}>{item}</li>)}
                </ul>
            )}
        </Block>
    );
}

function SignalReasons({ items, tone }: { items: string[]; tone: string }) {
    if (items.length === 0) return null;
    return (
        <ul className={`list-disc pl-5 text-xs space-y-0.5 ${tone}`}>
            {items.map((item) => <li key={item}>{item}</li>)}
        </ul>
    );
}

export function SignalQualityEvidence({
    explainability,
}: {
    explainability: EcgExplainability;
}) {
    const quality: EcgSignalQualityExplanation = explainability.signalQuality;
    const attribution = explainability.attribution;
    const leads = quality.leads;
    return (
        <Block title="Сигнал и вход модели">
            <div className="text-sm">
                Отведений прослежено: {leads.traced} из {leads.expected}
                {' · '}минимальная длительность: {fmtSeconds(quality.duration.actualMinS)}
                {quality.duration.requiredS !== null && (
                    <span className="text-muted-foreground"> (модель ждёт {fmtSeconds(quality.duration.requiredS)})</span>
                )}
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                <span>калибровка: {quality.calibrationFound ? 'найдена' : 'не найдена'}</span>
                {quality.duration.shortfallS !== null && quality.duration.shortfallS > 0 && (
                    <span>недостаток: {fmtSeconds(quality.duration.shortfallS)}</span>
                )}
                <span>
                    вход модели: {quality.adequateForModel ? 'достаточен' : 'недостаточен'}
                    {quality.modelInput.zeroPadded ? ' · дополнен нулями' : ''}
                </span>
            </div>
            {leads.missing.length > 0 && (
                <div className="text-xs text-muted-foreground">
                    Нет отведений: {leads.missing.join(', ')}
                </div>
            )}
            {leads.short.length > 0 && (
                <div className="text-xs text-muted-foreground">
                    Короткие отведения: {leads.short.join(', ')}
                </div>
            )}
            <SignalReasons items={quality.reasons} tone="text-amber-700 dark:text-amber-400" />
            <SignalReasons items={quality.warnings} tone="text-muted-foreground" />
            <div className="text-xs">
                Вклад отведений и участков времени:{' '}
                {attribution.available
                    ? `доступен${attribution.method ? ` (${attribution.method})` : ''}`
                    : 'недоступен'}
                {!attribution.available && attribution.reason && (
                    <span className="text-muted-foreground"> · {attribution.reason}</span>
                )}
            </div>
        </Block>
    );
}

const EVIDENCE_SOURCE_LABELS: Record<string, string> = {
    measurement: 'измерение',
    model: 'модель',
    attribution: 'атрибуция',
    quality: 'качество',
};

function fmtShare(value: number): string {
    if (!Number.isFinite(value)) return '—';
    return `${Math.round(value * 1000) / 10}%`;
}

function fmtWindowShare(item: EcgAttributionSegment): string {
    return `${Math.round(item.startMs)}–${Math.round(item.endMs)} мс ${fmtShare(item.share)}`;
}

export function RationaleEvidence({ rationale }: { rationale: EcgRationale }) {
    const hasContent = Boolean(
        rationale.summary
        || rationale.findings.length > 0
        || rationale.evidence.length > 0,
    );
    if (!hasContent) return null;
    return (
        <Block title="Обоснование">
            {rationale.summary && <div className="text-sm">{rationale.summary}</div>}
            {!rationale.available && rationale.reason && (
                <div className="text-xs text-amber-700 dark:text-amber-400">
                    Причина: {rationale.reason}
                </div>
            )}
            {rationale.findings.length > 0 && (
                <ul className="space-y-1">
                    {rationale.findings.map((item) => (
                        <li key={item.code} className="border rounded-md px-3 py-2">
                            <div className="text-xs font-medium">
                                {item.code}
                                {!item.available && ' · обоснование недоступно'}
                            </div>
                            <div className="text-xs">{item.summary}</div>
                        </li>
                    ))}
                </ul>
            )}
            {rationale.evidence.length > 0 && (
                <details className="text-xs">
                    <summary className="cursor-pointer text-muted-foreground">
                        Источники evidence ({rationale.evidence.length})
                    </summary>
                    <ul className="mt-1 space-y-0.5">
                        {rationale.evidence.map((item, index) => (
                            <li key={`${item.source}:${item.code}:${index}`}>
                                <span className="text-muted-foreground">
                                    [{EVIDENCE_SOURCE_LABELS[item.source] || item.source}]
                                </span>{' '}
                                {item.text}
                            </li>
                        ))}
                    </ul>
                </details>
            )}
        </Block>
    );
}

export function AttributionEvidence({ attribution }: { attribution: EcgAttribution }) {
    if (!attribution.available) {
        return (
            <Block title="Вклад модели">
                <div className="text-sm text-muted-foreground">
                    Атрибуция недоступна{attribution.reason ? `: ${attribution.reason}` : ''}.
                </div>
            </Block>
        );
    }
    const findings = attribution.findings.filter((item) => item.available);
    return (
        <Block title="Вклад модели">
            <div className="text-[11px] text-muted-foreground">
                {attribution.method || 'метод не указан'}
                {' · '}базовая линия {attribution.baseline || '—'}
                {attribution.method === 'integrated_gradients'
                    ? ` · шагов ${attribution.steps}`
                    : ''}
                {attribution.completeness && attribution.completeness.maxAbsError !== null
                    ? ` · ошибка полноты ≤ ${attribution.completeness.maxAbsError.toFixed(3)}`
                    : ''}
            </div>
            {findings.length === 0 ? (
                <div className="text-xs text-muted-foreground">
                    {attribution.degenerate
                        ? 'Вклад не выражен: все значения нулевые.'
                        : 'Нет модельных находок для атрибуции.'}
                </div>
            ) : (
                <ul className="space-y-1">
                    {findings.map((item) => (
                        <li key={item.code} className="border rounded-md px-3 py-2 space-y-0.5">
                            <div className="text-xs font-medium">
                                {item.code}
                                {item.probability !== null
                                    ? ` · вероятность ${item.probability.toFixed(3)}`
                                    : ''}
                            </div>
                            {item.degenerate ? (
                                <div className="text-xs text-muted-foreground">
                                    Вклад не выражен: все значения нулевые.
                                </div>
                            ) : (
                                <>
                                    <div className="text-xs">
                                        Отведения:{' '}
                                        {item.topLeads.length > 0
                                            ? item.topLeads
                                                .map((lead) => `${lead.lead} ${fmtShare(lead.share)}`)
                                                .join(', ')
                                            : '—'}
                                    </div>
                                    <div className="text-xs">
                                        Окна:{' '}
                                        {item.topTimeSegments.length > 0
                                            ? item.topTimeSegments.map(fmtWindowShare).join(', ')
                                            : '—'}
                                    </div>
                                </>
                            )}
                            {item.occlusionCheck && (
                                <div className="text-[11px] text-muted-foreground">
                                    Окклюзия: топ-1 отведение{' '}
                                    {item.occlusionCheck.leadsTop1Match ? 'совпало' : 'не совпало'}
                                    {item.occlusionCheck.leadsRankCorrelation !== null
                                        ? `, корреляция ${item.occlusionCheck.leadsRankCorrelation.toFixed(2)}`
                                        : ''}
                                </div>
                            )}
                            {item.fallbackReason && (
                                <div className="text-[11px] text-amber-700 dark:text-amber-400">
                                    Резерв: {item.fallbackReason}
                                </div>
                            )}
                            {item.reason && (
                                <div className="text-[11px] text-muted-foreground">
                                    Причина: {item.reason}
                                </div>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </Block>
    );
}
